import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, ChevronDown, FileText, Loader2, Pencil, RotateCcw, Save, Sparkles, Trash2 } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import BlockEditor from '../../components/b2b/BlockEditor'
import DemoBadge from '../../components/b2b/DemoBadge'
import DemoBanner from '../../components/b2b/DemoBanner'
import { api, APIError, extractErrorMessage } from '../../api/client'
import { BARS_SCALE_LABELS, barsLevelLabel, getFitMeta } from '../../utils/b2bFit'

const POLL_INTERVAL_MS = 5000

const CANDIDATE_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
]
const statusLabel = (s) => CANDIDATE_STATUSES.find((x) => x.value === s)?.label || 'Pending'

export default function CandidateDetailScreen() {
  const navigate = useNavigate()
  const { jobId, candidateId } = useParams()
  const [candidate, setCandidate] = useState(null)
  const [screening, setScreening] = useState(null)
  const [fit, setFit] = useState(null)
  const [analysis, setAnalysis] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [retriggeringFit, setRetriggeringFit] = useState(false)
  const [fitTriggerError, setFitTriggerError] = useState('')
  const [analysisTriggerError, setAnalysisTriggerError] = useState('')
  const [showDelete, setShowDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [editing, setEditing] = useState(false)
  const fitTimerRef = useRef(null)
  const analysisTimerRef = useRef(null)

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.delete(`/api/b2b/candidates/${candidateId}`)
      navigate(`/b2b/jobs/${jobId}`, { replace: true })
    } catch {
      setDeleting(false)
      setShowDelete(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.get(`/api/b2b/candidates/${candidateId}`),
      api.get(`/api/b2b/screenings/by-pair/${jobId}/${candidateId}`).catch(() => null),
      api.get(`/api/b2b/jobs/${jobId}/candidates/${candidateId}/fit-analysis`).catch(() => null),
    ])
      .then(([candData, scrData, fitData]) => {
        if (cancelled) return
        setCandidate(candData)
        setScreening(scrData)
        setFit(fitData)
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof APIError && err.status === 404) setError('Candidate not found.')
        else setError('Could not load candidate.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [jobId, candidateId])

  // Poll fit-analysis while pending/processing.
  useEffect(() => {
    if (!fit) return
    if (fit.status !== 'pending' && fit.status !== 'processing') return
    fitTimerRef.current = setTimeout(async () => {
      try {
        const next = await api.get(`/api/b2b/jobs/${jobId}/candidates/${candidateId}/fit-analysis`)
        setFit(next)
      } catch (err) {
        // Transient network blip → retry next tick. Log for DevTools so a
        // persistent failure doesn't get silently lost.
        console.warn('[fit-analysis] poll error, retrying next tick', err)
      }
    }, POLL_INTERVAL_MS)
    return () => { if (fitTimerRef.current) clearTimeout(fitTimerRef.current) }
  }, [fit, jobId, candidateId])

  // Pull screening analysis (summary + BARS) when screening completes.
  useEffect(() => {
    if (!screening?.id) { setAnalysis(null); return }
    if (screening.status !== 'completed') { setAnalysis(null); return }
    let cancelled = false
    api.get(`/api/b2b/screenings/${screening.id}/analysis`)
      .then((data) => { if (!cancelled) setAnalysis(data) })
      .catch((err) => {
        // 404 is expected when analysis was never enqueued — surface only real errors.
        if (err instanceof APIError && err.status !== 404) {
          console.warn('[screening-analysis] initial fetch error', err)
        }
      })
    return () => { cancelled = true }
  }, [screening?.id, screening?.status])

  // Poll screening analysis while pending/processing.
  useEffect(() => {
    if (!analysis || !screening?.id) return
    if (analysis.status !== 'pending' && analysis.status !== 'processing') return
    analysisTimerRef.current = setTimeout(async () => {
      try {
        const next = await api.get(`/api/b2b/screenings/${screening.id}/analysis`)
        setAnalysis(next)
      } catch (err) {
        console.warn('[screening-analysis] poll error, retrying next tick', err)
      }
    }, POLL_INTERVAL_MS)
    return () => { if (analysisTimerRef.current) clearTimeout(analysisTimerRef.current) }
  }, [analysis, screening?.id])

  const handleRetriggerFit = async () => {
    setRetriggeringFit(true)
    setFitTriggerError('')
    try {
      const next = await api.post(`/api/b2b/jobs/${jobId}/candidates/${candidateId}/fit-analysis`, {})
      setFit(next)
    } catch (err) {
      const message = err instanceof APIError ? extractErrorMessage(err.data) : err.message
      setFitTriggerError(message || 'Could not start fit analysis. Please retry.')
    } finally {
      setRetriggeringFit(false)
    }
  }

  const handleTriggerAnalysis = async () => {
    if (!screening?.id) return
    setAnalysisTriggerError('')
    try {
      const next = await api.post(`/api/b2b/screenings/${screening.id}/analysis`, {})
      setAnalysis(next)
    } catch (err) {
      const message = err instanceof APIError ? extractErrorMessage(err.data) : err.message
      setAnalysisTriggerError(message || 'Could not start screening analysis. Please retry.')
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div className="px-6 md:px-10 py-12 max-w-3xl mx-auto"><p className="text-sm text-ink-3">Loading…</p></div>
      </AppShell>
    )
  }

  if (error || !candidate) {
    return (
      <AppShell>
        <div className="px-6 md:px-10 py-12 max-w-3xl mx-auto">
          <Link to={`/b2b/jobs/${jobId}`} className="inline-flex items-center gap-2 text-sm text-ink-3 hover:text-ink-1">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error || 'Candidate not found.'}
          </div>
        </div>
      </AppShell>
    )
  }

  if (editing) {
    return (
      <AppShell>
        <div className="px-6 md:px-10 py-8 md:py-12 max-w-3xl mx-auto">
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="inline-flex items-center gap-2 text-sm text-ink-3 hover:text-ink-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to candidate
          </button>
          <h1 className="mt-4 text-2xl md:text-3xl font-bold text-ink-1">Edit candidate</h1>
          <p className="text-sm text-ink-3 mt-1">
            Update the candidate's details and resume sections. Add new sections with <em>Add section</em>.
          </p>
          <CandidateEditForm
            candidate={candidate}
            onCancel={() => setEditing(false)}
            onSaved={(updated) => { setCandidate(updated); setEditing(false) }}
          />
        </div>
      </AppShell>
    )
  }

  const completedQuestions = screening?.status === 'completed'
    ? (screening.questions || []).filter((q) => q.answer_text != null)
    : []
  // "Ready" = the AI report/BARS finished (not queued, running, or errored).
  const analysisReady = !!analysis
    && analysis.status !== 'pending'
    && analysis.status !== 'processing'
    && analysis.status !== 'failed'

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-6xl mx-auto">
        <Link to={`/b2b/jobs/${jobId}`} className="inline-flex items-center gap-2 text-sm text-ink-3 hover:text-ink-1">
          <ArrowLeft className="w-4 h-4" />
          Back to job
        </Link>

        <div className="mt-4 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-ink-1">{candidate.name}</h1>
              {candidate.is_demo && <DemoBadge />}
              <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-canvas text-ink-3">
                {statusLabel(candidate.status)}
              </span>
            </div>
            <p className="text-sm text-ink-3 mt-1">{candidate.email || 'No email'}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 border border-black/10 bg-white hover:bg-canvas text-ink-1 font-semibold text-sm px-3 min-h-[40px] rounded-xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              <Pencil className="w-4 h-4" />
              Edit candidate
            </button>
            <ScreeningBadge screening={screening} />
          </div>
        </div>

        {candidate.is_demo && (
          <div className="mt-6">
            <DemoBanner>
              <strong>This is a sample candidate</strong> we added so you can try the screening flow without uploading your own. Click <em>Set up screening</em> below to generate questions, or delete this candidate when ready.
            </DemoBanner>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link
            to={`/b2b/jobs/${jobId}/candidates/${candidateId}/screening`}
            className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 min-h-[44px] rounded-2xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            <FileText className="w-4 h-4" />
            {screening ? 'View / edit screening' : 'Set up screening'}
          </Link>
        </div>

        {showDelete && (
          <div
            className="fixed inset-0 z-40 flex items-center justify-center px-4"
            style={{ background: 'rgba(15,23,42,0.55)' }}
          >
            <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-ink-1">Delete this candidate?</h3>
                  <p className="mt-1 text-sm text-ink-2">
                    This permanently removes <strong>{candidate.name}</strong>, their resume, and any screening data on this job. This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowDelete(false)}
                  disabled={deleting}
                  className="px-4 py-2 rounded-xl text-sm font-semibold text-ink-2 hover:bg-canvas"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 rounded-xl text-sm font-bold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  {deleting ? 'Deleting…' : 'Permanently delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Single column. Order: AI screening (resume + answers screening) →
            BARS scorecard → screening answers → collapsible resume. */}
        <AiScreeningBlock
          fit={fit}
          analysis={analysis}
          screeningCompleted={screening?.status === 'completed'}
          onRetriggerFit={handleRetriggerFit}
          retriggeringFit={retriggeringFit}
          fitTriggerError={fitTriggerError}
          onTriggerAnalysis={handleTriggerAnalysis}
          analysisTriggerError={analysisTriggerError}
        />

        {analysisReady && analysis.bars_scorecard && (
          <div className="mt-6">
            <BarsScorecardCard scorecard={analysis.bars_scorecard} />
          </div>
        )}

        {completedQuestions.length > 0 && (
          <div className="mt-10">
            <ScreeningAnswers questions={completedQuestions} />
          </div>
        )}

        <div className="mt-10">
          <ResumeSection blocks={candidate.resume_blocks} />
        </div>

        <div className="mt-12 rounded-2xl border border-red-100 bg-red-50/40 p-5">
          <h2 className="text-xs font-bold uppercase tracking-wider text-red-700">Danger zone</h2>
          <p className="mt-1 text-sm text-ink-2">
            Permanently remove this candidate, their resume, and any screening data on this job. This cannot be undone.
          </p>
          <button
            type="button"
            onClick={() => setShowDelete(true)}
            className="mt-3 inline-flex items-center gap-2 border border-red-200 text-red-600 hover:bg-red-100 font-semibold px-4 min-h-[44px] rounded-2xl transition text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete candidate
          </button>
        </div>
      </div>
    </AppShell>
  )
}

/**
 * CandidateEditForm — edit name, email, status, and resume blocks.
 * Owns its own draft state (seeded from `candidate`), normalizes the payload
 * to satisfy backend validation, PATCHes, and reports the result via onSaved.
 */
function CandidateEditForm({ candidate, onCancel, onSaved }) {
  const [name, setName] = useState(candidate.name || '')
  const [email, setEmail] = useState(candidate.email || '')
  const [statusVal, setStatusVal] = useState(candidate.status || 'pending')
  const [blocks, setBlocks] = useState(() =>
    (candidate.resume_blocks || []).map((b) => ({
      id: String(b.id),
      label: b.label || '',
      content: b.content || '',
    })),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    setError('')
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    setSaving(true)
    try {
      const updated = await api.patch(`/api/b2b/candidates/${candidate.id}`, {
        name: name.trim(),
        email: email.trim() || null,
        status: statusVal,
        resume_blocks: blocks.map((b, idx) => ({
          label: b.label.trim() || `Section ${idx + 1}`,
          content: b.content || '',
          position: idx,
        })),
      })
      onSaved(updated)
    } catch (err) {
      let message = err instanceof APIError ? extractErrorMessage(err.data) : err.message
      if (err instanceof APIError && err.status === 422) {
        message = 'Please enter a valid email address.'
      }
      setError(message || 'Could not save changes.')
      setSaving(false)
    }
  }

  return (
    <>
      {error && (
        <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="mt-8 space-y-4">
        <Field label="Full name">
          <input
            type="text"
            placeholder="e.g., Mira Chen"
            className="flex-1 text-[15px] outline-none bg-transparent"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
          />
        </Field>

        <Field label="Email" hint="Used for sending the screening link.">
          <input
            type="email"
            placeholder="candidate@example.com"
            className="flex-1 text-[15px] outline-none bg-transparent"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={saving}
          />
        </Field>

        <Field label="Status">
          <select
            className="flex-1 text-[15px] outline-none bg-transparent"
            value={statusVal}
            onChange={(e) => setStatusVal(e.target.value)}
            disabled={saving}
          >
            {CANDIDATE_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-10">
        <h2 className="text-sm font-bold uppercase tracking-wider text-ink-3 mb-4">Resume</h2>
        <BlockEditor
          blocks={blocks}
          onChange={setBlocks}
          addLabel="Add section"
          contentPlaceholder="Paste content from CV here…"
        />
      </div>

      <div className="mt-8 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-5 py-2.5 rounded-2xl text-sm font-semibold text-ink-2 hover:bg-canvas transition disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 py-2.5 rounded-2xl transition disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </>
  )
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-ink-2 uppercase tracking-wide ml-1">{label}</label>
      <div className="flex items-center gap-3 rounded-2xl border border-black/10 bg-white px-4 py-3.5 focus-within:border-accent transition-colors">
        {children}
      </div>
      {hint && <p className="text-[11px] text-ink-3 ml-1">{hint}</p>}
    </div>
  )
}

function TriggerErrorPlate({ message }) {
  if (!message) return null
  return (
    <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
      {message}
    </div>
  )
}

function ResumeSection({ blocks }) {
  const list = blocks || []
  return (
    <details className="group bg-white rounded-3xl shadow-card border border-black/5">
      <summary className="cursor-pointer list-none flex items-center justify-between gap-3 px-6 py-5">
        <span className="text-sm font-bold uppercase tracking-wider text-ink-3">
          Resume {list.length > 0 && <span className="text-ink-3/60">({list.length})</span>}
        </span>
        <ChevronDown className="w-4 h-4 text-ink-3 transition-transform group-open:rotate-180" />
      </summary>
      <div className="px-6 pb-6 border-t border-black/5 pt-5">
        {list.length === 0 ? (
          <p className="text-sm text-ink-3">No resume yet.</p>
        ) : (
          <div className="space-y-3">
            {list.map((block) => (
              <div key={block.id} className="rounded-2xl border border-black/5 bg-canvas p-5">
                <h3 className="text-xs font-bold uppercase tracking-wider text-ink-3">{block.label}</h3>
                <p className="mt-2 text-[14px] text-ink-1 whitespace-pre-wrap">{block.content || '—'}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </details>
  )
}

function ScreeningAnswers({ questions }) {
  return (
    <section>
      <h2 className="text-sm font-bold uppercase tracking-wider text-ink-3 mb-4">Screening answers</h2>
      <div className="space-y-3">
        {questions.map((q) => (
          <details key={q.id} className="bg-white rounded-2xl shadow-card border border-black/5 group">
            <summary className="cursor-pointer px-5 py-4 list-none flex items-start gap-3">
              <span className="text-[11px] font-bold text-ink-3 uppercase tracking-wider mt-0.5">Q{q.position + 1}</span>
              <p className="flex-1 text-sm font-bold text-ink-1">{q.text}</p>
            </summary>
            <div className="px-5 pb-5 border-t border-black/5 pt-4 ml-9">
              <p className="text-[14px] text-ink-1 whitespace-pre-wrap">{q.answer_text}</p>
            </div>
          </details>
        ))}
      </div>
    </section>
  )
}

/**
 * Combined "AI screening" card. Two clearly-separated sub-sections so the
 * recruiter can tell the two AI passes apart:
 *   • Resume screening  — AI fit of the CV against the job (no re-run here)
 *   • Answer screening  — AI report on the candidate's screening answers
 * Answer screening only appears once the screening is completed.
 */
function AiScreeningBlock({
  fit,
  analysis,
  screeningCompleted,
  onRetriggerFit,
  retriggeringFit,
  fitTriggerError,
  onTriggerAnalysis,
  analysisTriggerError,
}) {
  if (!fit && !screeningCompleted) return null
  return (
    <div className="mt-8 bg-white rounded-3xl shadow-card border border-black/5 p-6">
      <div className="flex items-center gap-3">
        <Sparkles className="w-4 h-4 text-accent" />
        <p className="text-xs font-bold uppercase tracking-wider text-ink-3">AI screening</p>
      </div>

      {fit && (
        <div className="mt-5">
          <ResumeScreeningBody
            fit={fit}
            onRetrigger={onRetriggerFit}
            retriggering={retriggeringFit}
            triggerError={fitTriggerError}
          />
        </div>
      )}

      {screeningCompleted && (
        <div className={fit ? 'mt-6 pt-6 border-t border-black/5' : 'mt-5'}>
          <AnswerScreeningBody
            analysis={analysis}
            onTrigger={onTriggerAnalysis}
            triggerError={analysisTriggerError}
          />
        </div>
      )}
    </div>
  )
}

function SubHeading({ children, badge }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <p className="text-base font-bold text-ink-1">{children}</p>
      {badge}
    </div>
  )
}

/**
 * Badge for the Answer-screening sub-section, mirroring the resume "Good fit"
 * pill but driven by the BARS overall-match score. Same thresholds/palette as
 * BarsScorecardCard so the colour reads consistently across the page.
 */
function OverallMatchBadge({ overall }) {
  const tier = overall >= 80
    ? { label: 'Strong', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' }
    : overall >= 60
      ? { label: 'Good', badge: 'bg-lime-50 text-lime-700 border-lime-200', dot: 'bg-lime-500' }
      : overall >= 40
        ? { label: 'Mixed', badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' }
        : { label: 'Weak', badge: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' }
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold px-3 py-1 rounded-full border ${tier.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${tier.dot}`} />
      {tier.label} · {overall}/100
    </span>
  )
}

/** Resume-screening sub-section (AI fit of CV ↔ job). No re-run button. */
function ResumeScreeningBody({ fit, onRetrigger, retriggering, triggerError }) {
  const meta = getFitMeta(fit.verdict)

  if (fit.status === 'pending' || fit.status === 'processing') {
    return (
      <div>
        <SubHeading>Resume screening</SubHeading>
        <div className="mt-3 flex items-center gap-3 text-ink-3">
          <Loader2 className="w-5 h-5 animate-spin text-accent" />
          <p className="text-sm">Reviewing this resume against the job… (~10s)</p>
        </div>
      </div>
    )
  }

  if (fit.status === 'failed') {
    return (
      <div>
        <SubHeading>Resume screening</SubHeading>
        <p className="mt-3 text-sm font-semibold text-red-600">Resume screening failed</p>
        {fit.error && <p className="mt-1 text-sm text-ink-3">{fit.error}</p>}
        <button
          type="button"
          onClick={onRetrigger}
          disabled={retriggering}
          className="mt-3 inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-4 py-2 rounded-xl transition disabled:opacity-50"
        >
          <RotateCcw className="w-4 h-4" />
          {retriggering ? 'Retrying…' : 'Retry'}
        </button>
        <TriggerErrorPlate message={triggerError} />
      </div>
    )
  }

  if (fit.status !== 'ready' || !meta) {
    return <SubHeading>Resume screening</SubHeading>
  }

  const badge = (
    <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold px-3 py-1 rounded-full border ${meta.badgeClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dotClass}`} />
      {meta.label}
    </span>
  )

  return (
    <div>
      <SubHeading badge={badge}>Resume screening</SubHeading>
      {fit.reasoning && (
        <p className="mt-3 text-[14px] text-ink-1 whitespace-pre-wrap leading-relaxed">{fit.reasoning}</p>
      )}
      {(fit.screening_topics || []).length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-ink-3 mb-2">
            Topics to probe in screening
          </p>
          <ul className="space-y-1.5">
            {fit.screening_topics.map((topic, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-ink-1">
                <span className="text-accent mt-0.5">→</span>
                <span>{topic}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/** Answer-screening sub-section (AI report on the candidate's answers). */
function AnswerScreeningBody({ analysis, onTrigger, triggerError }) {
  if (!analysis) {
    return (
      <div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-base font-bold text-ink-1">Answer screening</p>
            <p className="mt-1 text-sm text-ink-3">Generate the recommendation, strengths, risks, and BARS scorecard.</p>
          </div>
          <button
            type="button"
            onClick={onTrigger}
            className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-4 py-2 rounded-xl transition"
          >
            <Sparkles className="w-4 h-4" />
            Generate report
          </button>
        </div>
        <TriggerErrorPlate message={triggerError} />
      </div>
    )
  }

  if (analysis.status === 'pending' || analysis.status === 'processing') {
    return (
      <div>
        <SubHeading>Answer screening</SubHeading>
        <div className="mt-3 flex items-center gap-3 text-ink-3">
          <Loader2 className="w-5 h-5 animate-spin text-accent" />
          <p className="text-sm">Generating report… (30-60s)</p>
        </div>
      </div>
    )
  }

  if (analysis.status === 'failed') {
    return (
      <div>
        <SubHeading>Answer screening</SubHeading>
        <p className="mt-3 text-sm font-semibold text-red-600">Answer screening failed</p>
        {analysis.error && <p className="mt-1 text-sm text-ink-3">{analysis.error}</p>}
        <button
          type="button"
          onClick={onTrigger}
          className="mt-3 inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-4 py-2 rounded-xl transition"
        >
          <RotateCcw className="w-4 h-4" />
          Retry
        </button>
        <TriggerErrorPlate message={triggerError} />
      </div>
    )
  }

  const overall = analysis.bars_scorecard?.overall_match
  return (
    <div>
      <SubHeading badge={overall != null ? <OverallMatchBadge overall={overall} /> : null}>
        Answer screening
      </SubHeading>
      {analysis.summary_md ? (
        <div className="prose prose-sm max-w-none text-ink-1 mt-3">
          {renderMarkdownBlocks(analysis.summary_md)}
        </div>
      ) : (
        <p className="mt-3 text-sm text-ink-3">No report content.</p>
      )}
    </div>
  )
}

function BarsScorecardCard({ scorecard }) {
  const overall = scorecard.overall_match ?? 0
  const overallColor = overall >= 80
    ? 'text-emerald-600'
    : overall >= 60
      ? 'text-lime-600'
      : overall >= 40
        ? 'text-amber-600'
        : 'text-red-600'

  return (
    <div className="bg-white rounded-3xl shadow-card border border-black/5 p-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Sparkles className="w-4 h-4 text-accent" />
          <p className="text-xs font-bold uppercase tracking-wider text-ink-3">BARS scorecard</p>
        </div>
        <div className="text-right">
          <p className={`text-3xl font-bold ${overallColor}`}>{overall}<span className="text-base text-ink-3">/100</span></p>
          <p className="text-[10px] uppercase tracking-wider font-bold text-ink-3">Overall match</p>
        </div>
      </div>

      {/* EU AI Act 2026 disclosure — informs the recruiter (and any
          regulator inspecting screenshots) that scores are AI-generated. */}
      <p className="mt-2 text-[11px] text-ink-3">
        Generated by AI (Llama 3.3 70B), reviewed by recruiter. Per-scale
        evidence below; full prompt + response hashed in audit log.
      </p>

      {scorecard.headline && (
        <p className="mt-3 text-sm text-ink-1 italic">"{scorecard.headline}"</p>
      )}

      <div className="mt-5 space-y-3">
        {Object.keys(BARS_SCALE_LABELS).map((idStr) => {
          const id = String(idStr)
          const entry = scorecard.scores?.[id]
          if (!entry) return null
          const meta = BARS_SCALE_LABELS[id]
          const score = Number(entry.score) || 0
          const pct = Math.max(0, Math.min(100, (score - 1) * 25))
          const barColor = score >= 4.5
            ? 'bg-emerald-500'
            : score >= 3.5
              ? 'bg-lime-500'
              : score >= 2.5
                ? 'bg-amber-500'
                : score >= 1.5
                  ? 'bg-orange-500'
                  : 'bg-red-500'
          return (
            <div key={id} className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-base">{meta.icon}</span>
                <p className="text-sm font-bold text-ink-1 flex-1">{meta.name}</p>
                <span className="text-xs font-bold text-ink-2">{score}/5</span>
                <span className="text-[10px] uppercase tracking-wider font-bold text-ink-3 w-32 text-right">
                  {barsLevelLabel(score)}
                </span>
              </div>
              <div className="h-1.5 bg-canvas rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
              </div>
              {entry.evidence && entry.evidence !== 'Insufficient signal' && (
                <p className="text-xs text-ink-3 mt-1.5 italic">"{entry.evidence}"</p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function renderMarkdownBlocks(md) {
  const lines = String(md || '').split('\n')
  const out = []
  let listBuf = []
  const flushList = () => {
    if (listBuf.length === 0) return
    out.push(
      <ul key={`l-${out.length}`} className="list-disc pl-5 mb-3 space-y-1.5 text-[14px] leading-relaxed">
        {listBuf.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
    )
    listBuf = []
  }
  lines.forEach((rawLine, i) => {
    const line = rawLine.trim()
    if (!line) { flushList(); return }
    if (/^##\s+/.test(line)) {
      flushList()
      out.push(<h3 key={`h-${i}`} className="text-sm font-bold text-ink-1 mt-4 mb-1.5">{line.replace(/^##\s+/, '')}</h3>)
    } else if (/^#\s+/.test(line)) {
      flushList()
      out.push(<h2 key={`h-${i}`} className="text-[15px] font-bold text-ink-1 mt-4 mb-1.5">{line.replace(/^#\s+/, '')}</h2>)
    } else if (/^[-*]\s+/.test(line)) {
      listBuf.push(line.replace(/^[-*]\s+/, ''))
    } else {
      flushList()
      out.push(<p key={`p-${i}`} className="text-[14px] leading-relaxed mb-2">{line}</p>)
    }
  })
  flushList()
  return out
}

function ScreeningBadge({ screening }) {
  if (!screening) {
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full bg-gray-100 text-gray-500">
        No screening
      </span>
    )
  }
  const styles = {
    draft: 'bg-gray-100 text-gray-500',
    sent: 'bg-amber-100 text-amber-700',
    completed: 'bg-green-100 text-green-700',
  }
  const label = screening.status === 'sent' ? 'Awaiting response' :
                screening.status === 'completed' ? 'Completed' : 'Draft'
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full ${styles[screening.status] || styles.draft}`}>
      {label}
    </span>
  )
}
